import type { EngineSnapshot, WaitingState } from '../engine/stepEngine';
import { StepEngine } from '../engine/stepEngine';
import type { PersistenceAdapter } from './persistence';
import type { ExtendedCertificate, RouteMapHotspotActions, StartScreenConfig } from 'shared';

export interface PlayerRuntimeOptions {
  engine: StepEngine;
  persistence: PersistenceAdapter;
  mode: 'mock' | 'live';
  context?: RuntimeContext;
}

export interface RuntimeContext {
  runId?: string;
  deviceKey?: string;
  ticketStatus?: string;
  scenarioTitle?: string;
}

export class PlayerRuntime {
  private readonly engine: StepEngine;
  private readonly persistence: PersistenceAdapter;
  private readonly mode: 'mock' | 'live';
  private readonly context?: RuntimeContext;
  private runCompleted = false;

  constructor(options: PlayerRuntimeOptions) {
    this.engine = options.engine;
    this.persistence = options.persistence;
    this.mode = options.mode;
    this.context = options.context;
  }

  getMode(): 'mock' | 'live' {
    return this.mode;
  }

  getContext(): RuntimeContext | undefined {
    return this.context;
  }

  getSnapshot(): EngineSnapshot {
    return this.engine.getSnapshot();
  }

  getWaitingState(): WaitingState | null {
    return this.engine.getWaitingState();
  }

  getStartScreen(): StartScreenConfig | undefined {
    return this.engine.getScenario().startScreen;
  }

  async advance(): Promise<EngineSnapshot> {
    const snapshot = this.engine.advance();
    if (!this.runCompleted) {
      await this.persistence.saveState(this.engine.getState());
    }
    return snapshot;
  }

  async resolveBranch(goto?: string): Promise<EngineSnapshot> {
    const snapshot = this.engine.resolveBranch(goto);
    if (!this.runCompleted) {
      await this.persistence.saveState(this.engine.getState());
    }
    return snapshot;
  }

  async submitMinigameResult(result: Record<string, unknown>): Promise<EngineSnapshot> {
    const waiting = this.engine.getWaitingState();
    if (!waiting || waiting.type !== 'minigame') {
      throw new Error('Minigame step is not active');
    }
    await this.persistence.recordMinigameResult(waiting.step, result);
    const snapshot = this.engine.completeMinigame(result);
    if (!this.runCompleted) {
      await this.persistence.saveState(this.engine.getState());
    }
    return snapshot;
  }

  async submitSpotGateAnswer(answer: string): Promise<EngineSnapshot> {
    const waiting = this.engine.getWaitingState();
    if (!waiting || waiting.type !== 'spot_gate') {
      throw new Error('Spot gate step is not active');
    }
    const snapshot = this.engine.submitSpotGateAnswer(answer);
    if (!this.runCompleted) {
      await this.persistence.saveState(this.engine.getState());
    }
    return snapshot;
  }

  async submitFreeInput(payload: {
    userText: string;
    suggestedRouteKey?: string;
    inputMeta?: {
      charCount: number;
      isSilent?: boolean;
      isTooShort?: boolean;
      isNonsense?: boolean;
    };
  }): Promise<EngineSnapshot> {
    const waiting = this.engine.getWaitingState();
    if (!waiting || waiting.type !== 'free_input') {
      throw new Error('Free input step is not active');
    }
    const response = await this.persistence.recordFreeInput(waiting.step, {
      userText: payload.userText,
      routes: waiting.step.routes ?? [],
      defaultRouteKey: waiting.step.defaultRouteKey ?? 'default',
      emptyRouteKey: waiting.step.emptyRouteKey ?? 'no_input',
      llmTimeoutMs: waiting.step.llmTimeoutMs ?? 15_000,
      suggestedRouteKey: payload.suggestedRouteKey,
      inputMeta: payload.inputMeta
    });
    const snapshot = this.engine.submitFreeInput(response.routeKey, response.assistantLines ?? []);
    if (!this.runCompleted) {
      await this.persistence.saveState(this.engine.getState());
    }
    return snapshot;
  }

  async skipFreeInput(routeKey?: string): Promise<EngineSnapshot> {
    const waiting = this.engine.getWaitingState();
    if (!waiting || waiting.type !== 'free_input') {
      throw new Error('Free input step is not active');
    }
    const fallbackRouteKey = routeKey ?? waiting.step.defaultRouteKey ?? 'default';
    const snapshot = this.engine.submitFreeInput(fallbackRouteKey, ['（通信が不安定みたいだ。いったん先へ進もう。）']);
    if (!this.runCompleted) {
      await this.persistence.saveState(this.engine.getState());
    }
    return snapshot;
  }

  async completeReward(endingId?: string): Promise<EngineSnapshot> {
    await this.persistence.completeRun(endingId);
    this.runCompleted = true;
    const snapshot = this.engine.completeReward(endingId);
    // NOTE: completeRun後はチケットがCOMPLETED状態になるため、saveStateを呼ばない
    // （呼ぶと410 ERR_TICKET_COMPLETEDエラーになる）
    return snapshot;
  }

  async completeEnding(endingId?: string): Promise<EngineSnapshot> {
    if (!this.runCompleted) {
      await this.persistence.completeRun(endingId);
      this.runCompleted = true;
    }
    return this.engine.completeEnding(endingId);
  }

  /**
   * Phase A-h: ダイアログの選択肢を選択
   */
  async selectDialogChoice(choiceIndex: number): Promise<EngineSnapshot> {
    const waiting = this.engine.getWaitingState();
    if (!waiting || waiting.type !== 'dialog_choice') {
      throw new Error('Dialog choice step is not active');
    }
    const snapshot = this.engine.selectDialogChoice(choiceIndex);
    if (!this.runCompleted) {
      await this.persistence.saveState(this.engine.getState());
    }
    return snapshot;
  }

  /**
   * LINE Dialog: 選択肢を選択 or autoAdvanceで次へ
   */
  async selectLineDialogChoice(choiceIndex?: number): Promise<EngineSnapshot> {
    const snapshot = this.engine.selectLineDialogChoice(choiceIndex);
    if (!this.runCompleted) {
      await this.persistence.saveState(this.engine.getState());
    }
    return snapshot;
  }

  /**
   * LINE Dialog: 自由入力を送信（Phase 1: API呼び出し + AI返信をフラグに保存）
   * LineDialogViewがAI返信アニメーション後にcompleteLineDialogを呼ぶ
   */
  async submitLineDialogFreeInput(userText: string): Promise<EngineSnapshot> {
    const waiting = this.engine.getWaitingState();
    if (!waiting || waiting.type !== 'line_dialog') {
      throw new Error('LINE dialog step is not active');
    }
    const step = waiting.step;
    const freeInputConfig = (step as any).reply?.freeInput;
    const routeKey = freeInputConfig?.defaultRouteKey ?? 'default';

    // 作者が用意した定型返信（followUpMessages）があればローカルでそのまま表示する。
    // AI/モックに委譲しないことで、live でも mock でも作者の返信が確実に流れて done へ到達する。
    const followUpMessages = (freeInputConfig?.followUpMessages ?? []) as Array<{ text?: string }>;
    const followUpLines = followUpMessages
      .map((m) => (m?.text ?? '').trim())
      .filter((text) => text.length > 0);

    if (followUpLines.length > 0) {
      const snapshot = this.engine.storeLineDialogAiReply(userText, routeKey, followUpLines);
      if (!this.runCompleted) {
        await this.persistence.saveState(this.engine.getState());
      }
      return snapshot;
    }

    // followUpMessages が無い場合は従来どおり free_input API（AI/モック）にフォールバック
    const response = await this.persistence.recordFreeInput(
      {
        id: step.id,
        type: 'free_input',
        title: step.title ?? '',
        notes: '',
        routes: freeInputConfig?.routes ?? [],
        defaultRouteKey: routeKey,
        emptyRouteKey: 'no_input',
        llmTimeoutMs: 15000,
        maxChars: freeInputConfig?.maxChars ?? 200,
        promptText: freeInputConfig?.systemPrompt ?? '',
      } as any,
      {
        userText,
        routes: freeInputConfig?.routes ?? [],
        defaultRouteKey: routeKey,
        emptyRouteKey: 'no_input',
        llmTimeoutMs: 15000,
        suggestedRouteKey: routeKey,
      }
    );
    const snapshot = this.engine.storeLineDialogAiReply(
      userText,
      response.routeKey,
      response.assistantLines ?? ['……']
    );
    if (!this.runCompleted) {
      await this.persistence.saveState(this.engine.getState());
    }
    return snapshot;
  }

  /**
   * LINE Dialog: AI返信アニメーション完了後に呼ばれる（Phase 2: 次ステップへ進行）
   */
  async completeLineDialog(): Promise<EngineSnapshot> {
    const snapshot = this.engine.completeLineDialog();
    if (!this.runCompleted) {
      await this.persistence.saveState(this.engine.getState());
    }
    return snapshot;
  }

  async discoverRouteMapHotspot(
    spotId: string,
    hotspotId: string,
    actions?: RouteMapHotspotActions
  ): Promise<EngineSnapshot> {
    const snapshot = this.engine.discoverRouteMapHotspot(spotId, hotspotId, actions);
    if (!this.runCompleted) {
      await this.persistence.saveState(this.engine.getState());
    }
    return snapshot;
  }

  async generateCertificate(): Promise<ExtendedCertificate> {
    return this.persistence.generateCertificate();
  }
}

